// inputValidator.js

/**
 * Comprehensive validation rules for the testimonial submission form
 * Each rule defines the validation requirements for a specific field
 */
const validationRules = {
  // User Information
  orderId: { label: 'Order ID', required: true },
  eventDate: { label: 'Event Date', required: true, type: 'date' },
  country: { label: 'Country', required: true },
  city: { label: 'City', required: false, trim: true },
  state: { label: 'State', required: false },

  // Wedding/Event Details
  eventType: { label: 'Event Type', required: true },
  storyBody: { label: 'Event Story', required: true, trim: true, minLength: 50 },

  // Photos and Videos
  dropSubmit: { label: 'Upload Photos', required: true, value: 'true' },
  imageArray: { label: 'Photos', required: true, nonEmpty: true },
  // videos are required as a field but don't need values
  videoArray: { label: 'Videos', required: false, allowEmpty: true },

  // Vendor Details
  eventFlowers: { label: 'Event Flowers', required: true, trim: true },
  eventPlanner: { label: 'Event Planner', required: false, trim: true },
  eventVenue: { label: 'Event Venue', required: false, trim: true },
  photographer: { label: 'Photographer', required: false, trim: true },
  videographer: { label: 'Videographer', required: false, trim: true },
  caterer: { label: 'Caterer', required: false, trim: true },

  // Terms Agreement
  termsAgreement: { label: 'Terms & Conditions', required: true, type: 'checkbox' },
};

/**
 * Checks if a value is empty based on its type and trim setting
 * @param {*} value - The value to check
 * @param {boolean} trim - Whether to trim the value before checking
 * @returns {boolean} - True if the value is empty, false otherwise
 */
const isEmpty = (value, trim) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && trim) return value.trim() === '';
  if (typeof value === 'string') return value === '';
  if (typeof value === 'boolean') return value === false;
  return false;
};

/**
 * Validates the length of a string
 * @param {string} value - The string to validate
 * @param {number} minLength - The minimum length required
 * @returns {boolean} - True if the string meets the minimum length, false otherwise
 */
const validateLength = (value, minLength) => {
  if (!minLength) return true;
  return value && value.length >= minLength;
};

/**
 * Validates a date value
 * @param {string} value - The date string to validate
 * @returns {boolean} - True if the date is valid, false otherwise
 */
const validateDate = value => {
  if (!value) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
};

/**
 * Validates that a comma-separated list is not empty
 * @param {string} value - The comma-separated string to validate
 * @returns {boolean} - True if the string contains at least one non-empty value
 */
const validateNonEmpty = value => {
  if (!value) return false;
  const items = value.split(',').filter(item => item.trim() !== '');
  return items.length > 0;
};

/**
 * Checks if the required checkboxes for a product have been selected
 * @param {Object} formData - The form data
 * @param {string} productId - The product ID
 * @returns {boolean} - True if at least one checkbox is selected
 */
const hasProductUsages = (formData, productId) => {
  const key = `proUsed_${productId}`;
  return formData[key] && formData[key].length > 0;
};

/**
 * Creates an error message for validation failures
 * @param {string[]} failedFields - Array of field labels that failed validation
 * @returns {object} - Error message object
 */
const createValidationMessage = failedFields => ({
  error: true,
  errorShowModal: true,
  headMsg: 'Required Fields Missing',
  bodyMsg: `Please complete the following required fields:<br/><b>${failedFields.join(
    '</b><br/><b>'
  )}</b>`,
});

/**
 * Creates a specific error message for image upload requirement
 * @returns {object} - Error message object
 */
const createImageValidationMessage = () => ({
  error: true,
  errorShowModal: true,
  headMsg: 'Photos Required',
  bodyMsg: 'Please upload at least one photo before submitting your story.',
});

/**
 * Validates a single form field
 * @param {string} name - Field name
 * @param {*} value - Field value
 * @param {boolean} checked - Checkbox checked state
 * @returns {object} - Validation result
 */
const validateField = (name, value, checked) => {
  const rule = validationRules[name];
  if (!rule || !rule.required) return { error: false };

  // For checkboxes
  if (rule.type === 'checkbox' && !checked) {
    return createValidationMessage([rule.label]);
  }

  // For date fields
  if (rule.type === 'date' && !validateDate(value)) {
    return createValidationMessage([rule.label]);
  }

  // For text fields with minimum length
  if (rule.minLength && !validateLength(value, rule.minLength)) {
    return createValidationMessage([`${rule.label} (minimum ${rule.minLength} characters)`]);
  }

  // For arrays that must contain values
  if (rule.nonEmpty && !validateNonEmpty(value)) {
    if (name === 'imageArray') {
      return createImageValidationMessage();
    }
    return createValidationMessage([rule.label]);
  }

  // Allow empty arrays for certain fields (like videos)
  if (rule.allowEmpty) {
    return { error: false };
  }

  // For general required fields
  if (isEmpty(value, rule.trim)) {
    return createValidationMessage([rule.label]);
  }

  // For dropSubmit with specific value
  if (name === 'dropSubmit' && rule.value && value !== rule.value) {
    return createImageValidationMessage();
  }

  return { error: false };
};

/**
 * Prepares the submission data with proper handling for videos and images
 * @param {object} formData - Form data object
 * @returns {object} - Prepared data for submission
 */
const prepareSubmissionData = formData => {
  const data = {
    images: {},
    videos: {}, // Always include videos object, even if empty
    products: [],
  };

  // Process images
  if (formData.imageArray && formData.imageArray.trim() !== '') {
    const images = formData.imageArray.split(',');
    images.forEach((image, index) => {
      if (image && image.trim() !== '') {
        data.images[index] = image.trim();
      }
    });
  }

  // Process videos (can be empty)
  if (formData.videoArray && formData.videoArray.trim() !== '') {
    const videos = formData.videoArray.split(',');
    videos.forEach((video, index) => {
      if (video && video.trim() !== '') {
        data.videos[index] = video.trim();
      }
    });
  }

  // Add other fields
  Object.entries(formData).forEach(([key, value]) => {
    if (
      key !== 'imageArray' &&
      key !== 'videoArray' &&
      !key.startsWith('proUsed_') &&
      !key.startsWith('review_') &&
      !key.startsWith('textreview_')
    ) {
      if (key === 'eventDate') {
        try {
          data[key] = new Date(value).toISOString();
        } catch (e) {
          data[key] = new Date().toISOString();
        }
      } else if (key === 'termsAgreement') {
        data[key] = value === true || value === 'true';
      } else if (value !== undefined && value !== null) {
        if (typeof value === 'string') {
          if (value.trim() !== '') {
            data[key] = value;
          }
        } else {
          data[key] = value;
        }
      }
    }
  });

  return data;
};

/**
 * Validates all form fields
 * @param {object} formData - Form data object
 * @returns {object} - Validation result
 */
const validateForm = formData => {
  // Image validation is critical - check it first
  if (
    isEmpty(formData.imageArray, false) ||
    !validateNonEmpty(formData.imageArray) ||
    formData.dropSubmit !== 'true'
  ) {
    return createImageValidationMessage();
  }

  const failedFields = [];

  // Validate other fields
  Object.entries(validationRules).forEach(([name, rule]) => {
    if (!rule.required || name === 'imageArray' || name === 'dropSubmit' || name === 'videoArray')
      return;

    const value = formData[name];
    const checked = rule.type === 'checkbox' ? formData[name] === true : undefined;

    if (rule.type === 'checkbox' && !checked) {
      failedFields.push(rule.label);
    } else if (rule.type === 'date' && !validateDate(value)) {
      failedFields.push(rule.label);
    } else if (rule.minLength && !validateLength(value, rule.minLength)) {
      failedFields.push(`${rule.label} (minimum ${rule.minLength} characters)`);
    } else if (rule.nonEmpty && !validateNonEmpty(value)) {
      failedFields.push(rule.label);
    } else if (isEmpty(value, rule.trim)) {
      failedFields.push(rule.label);
    }
  });

  return failedFields.length > 0 ? createValidationMessage(failedFields) : { error: false };
};

/**
 * Process the complete form data for submission
 * @param {FormData} formElements - Form elements from event.target.elements
 * @returns {Object|null} - Processed form data or null if validation fails
 */
const processFormData = formElements => {
  // Create form data object for validation
  const formData = {};

  // Collect all form values
  for (let i = 0; i < formElements.length; i++) {
    const element = formElements[i];
    const { name, value, checked, type } = element;

    if (!name) continue;

    if (type === 'checkbox') {
      formData[name] = checked;
    } else {
      formData[name] = value;
    }
  }

  // Perform form validation
  const validationResult = validateForm(formData);

  // Return null if validation fails
  if (validationResult.error) {
    return { error: true, validation: validationResult };
  }

  // Process product data
  const productsData = [];
  const productIds = Object.keys(formData)
    .filter(key => key.startsWith('proUsed_'))
    .map(key => key.replace('proUsed_', ''));

  productIds.forEach(productId => {
    const productUsedValue = formData[`proUsed_${productId}`];
    const productUsed = productUsedValue
      ? productUsedValue.split(',').map(item => parseInt(item.trim(), 10))
      : [6]; // Default value

    const productReview = formData[`textreview_${productId}`] || '';
    const productRate = parseInt(formData[`review_${productId}`] || 0, 10);

    productsData.push({
      productId,
      productRate,
      productUsed,
      productReview,
    });
  });

  // Prepare final submission data with proper handling for required fields
  const submissionData = {
    images: {},
    videos: {}, // Always include videos object, even if empty
    products: productsData,
  };

  // Process images array
  if (formData.imageArray) {
    const imageArray = formData.imageArray.split(',');
    imageArray.forEach((image, index) => {
      if (image && image.trim() !== '') {
        submissionData.images[index] = image.trim();
      }
    });
  }

  // Process videos array - must always be included even if empty
  if (formData.videoArray) {
    const videoArray = formData.videoArray.split(',');
    videoArray.forEach((video, index) => {
      if (video && video.trim() !== '') {
        submissionData.videos[index] = video.trim();
      }
    });
  }

  // Process remaining form fields
  Object.entries(formData).forEach(([name, value]) => {
    // Skip already processed fields
    if (
      name === 'imageArray' ||
      name === 'videoArray' ||
      name.includes('proUsed_') ||
      name.includes('review_') ||
      name.includes('textreview_')
    ) {
      return;
    }

    // Handle special fields
    switch (name) {
      case 'eventDate':
        try {
          submissionData[name] = new Date(value).toISOString();
        } catch (e) {
          submissionData[name] = new Date().toISOString();
        }
        break;

      case 'termsAgreement':
        // Handle checkbox value
        submissionData[name] = value === true || value === 'true';
        break;

      default:
        // Only include non-empty values
        if (value !== undefined && value !== null) {
          if (typeof value === 'string') {
            if (value.trim() !== '') {
              submissionData[name] = value;
            }
          } else {
            submissionData[name] = value;
          }
        }
        break;
    }
  });

  return submissionData;
};

/**
 * Format the storyBody character count
 * @param {string} value - The story text
 * @param {number} minLength - Minimum required length
 * @returns {object} - The formatted count and validity
 */
const formatStoryCharCount = (value, minLength = 50) => {
  const count = value ? value.length : 0;
  const isValid = count >= minLength;

  return {
    count,
    isValid,
    formatted: `${count}/${minLength}`,
    className: isValid ? 'char-counter' : 'char-counter char-counter-error',
  };
};

/**
 * Check if form has unsaved changes
 * @param {object} initialFormData - Initial form data
 * @param {object} currentFormData - Current form data
 * @returns {boolean} - True if there are unsaved changes
 */
const hasUnsavedChanges = (initialFormData, currentFormData) => {
  if (!initialFormData || !currentFormData) return false;

  // Check text fields and selects
  for (const key in currentFormData) {
    if (
      key !== 'imageArray' &&
      key !== 'videoArray' &&
      !key.startsWith('proUsed_') &&
      currentFormData[key] !== initialFormData[key]
    ) {
      return true;
    }
  }

  // Check for new images or videos
  if (
    (currentFormData.imageArray && currentFormData.imageArray !== initialFormData.imageArray) ||
    (currentFormData.videoArray && currentFormData.videoArray !== initialFormData.videoArray)
  ) {
    return true;
  }

  return false;
};

/**
 * Helper for validating product usage checkboxes
 * @param {string} productId - The product ID
 * @param {Array} checkedBoxes - Array of checkbox IDs that are checked
 * @returns {string} - Comma-separated list of checked values
 */
const processProductUsage = (productId, checkedBoxes) => {
  if (!checkedBoxes || checkedBoxes.length === 0) {
    return '6'; // Default to "Other" if nothing selected
  }
  return checkedBoxes.join(',');
};

// Export all validation functions
export const inputValidator = (name, value, checked) => {
  return validateField(name, value, checked);
};

export const formValidator = formData => {
  return validateForm(formData);
};

export const processForm = formElements => {
  return processFormData(formElements);
};

export const prepareData = formData => {
  return prepareSubmissionData(formData);
};

export const storyCharCounter = value => {
  return formatStoryCharCount(value);
};

export const checkUnsavedChanges = (initialData, currentData) => {
  return hasUnsavedChanges(initialData, currentData);
};

export const processProductCheckboxes = (productId, checkedBoxes) => {
  return processProductUsage(productId, checkedBoxes);
};

export const getValidationRules = () => {
  return validationRules;
};
